import { LocationStrategy } from '@angular/common';
import {
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
} from '@angular/common/http';
import { Injectable, NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { JwtModule } from '@auth0/angular-jwt';
import { NgUiKitModule } from '@infocert/ng-ui-kit';

import { StoreModule } from '@ngrx/store';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { FORMLY_CONFIG, FormlyModule } from '@ngx-formly/core';
import {
  MissingTranslationHandler,
  MissingTranslationHandlerParams,
  TranslateLoader,
  TranslateModule,
  TranslateService,
} from '@ngx-translate/core';
import { SharedModule } from '@shared';
import { AltTranslateDirective } from '@app/translations/i18n/alt-translate.directive';
import { OAuthModule } from 'angular-oauth2-oidc';
import {
  RECAPTCHA_V3_SITE_KEY,
  RecaptchaModule,
  RecaptchaV3Module,
} from 'ng-recaptcha-2';

import { CookieService } from 'ngx-cookie-service';
import { ToastNoAnimationModule } from 'ngx-toastr';
import {
  provideNgxWebstorage,
  withLocalStorage,
  withNgxWebstorageConfig,
  withSessionStorage,
} from 'ngx-webstorage';
import { catchError, forkJoin, map, Observable, of } from 'rxjs';
import { BreadcrumbModule } from 'xng-breadcrumb';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { BreadcrumbsComponent } from './components/commons/breadcrumbs/breadcrumbs.component';
import { CommonsModule } from './components/commons/commons.module';
import { registerTranslateExtension } from './components/commons/forms/formly-extension';
import {
  pinPukValidator,
  SameNewAndConfirmPassword,
  sameOldPassword,
  SamePinAndConfirmPin,
  samePinAndPuk,
  strenghtPasswordMinosse,
  strenghtPasswordValidatorFn,
} from './components/commons/forms/formly-validators';
import { UiKitFieldWrapper } from './components/commons/forms/formly-wrapper-panel';
import { InputFieldType } from './components/commons/forms/input-field-type';
import { InputOtpType } from './components/commons/forms/input-otp-type';
import { InputPassworddType } from './components/commons/forms/input-password-type';
import { CommonModalComponent } from './components/commons/modal/common.modal.component';
import { FooterComponent } from './components/footer/footer.component';
import { ContactSupportComponent } from './components/header/contact-support/contact-support.component';
import { HeaderComponent } from './components/header/header.component';
import { MigrationBannerComponent } from './components/migration/migration-banner/migration-banner.component';
import { MigrationComponent } from './components/migration/migration/migration.component';
import { ActionReportPageComponent } from './components/routes/action-report/action-report.component';
import { FirstEmisPageComponent } from './components/routes/activation-steps//first-emis/first-emis.component';
import { ActivationStepsComponent } from './components/routes/activation-steps/activation-steps.component';
import { ForcePWDPageComponent } from './components/routes/activation-steps/force-pwd/force-pwd.component';
import { CertificatePageComponent } from './components/routes/certificate/certificate.component';
import { ErrorPageComponent } from './components/routes/error/error.component';
import { EsgComponent } from './components/routes/esg/esg.component';
import { HomePageComponent } from './components/routes/homepage/homepage.component';
import { LoginPageComponent } from './components/routes/login/login.component';
import { BccFederationComponent } from './components/routes/bcc/bcc.component';
import { ManageCertComponent } from './components/routes/manage-cert/manage-cert.component';
import { MultiCertComponent } from './components/routes/multi-cert/multi-cert.component';
import { Oauth2Component } from './components/routes/oauth2/oauth2.component';
import { OtpPageComponent } from './components/routes/otp/otp.component';
import { VirtualTokenFormComponent } from './components/routes/otp/virtual-token-form/virtual-token-form.component';
import { VirtualTokenComponent } from './components/routes/otp/virtual-token/virtual-token.component';
import { PinPageComponent } from './components/routes/pin/pin.component';
import { RenewSuccessComponent } from './components/routes/renew/renew-success/renew-success.component';
import { RenewPageComponent } from './components/routes/renew/renew.component';
import { ReportPageComponent } from './components/routes/report/report.component';
import { SuspendPageComponent } from './components/routes/suspend/suspend.component';
import { TestPageComponent } from './components/routes/test/test.component';
import { ErrorModalComponent } from './error-modal/error-modal.component';
import { AuthGuard } from './guards/Auth.guard';
import { LivechatComponent } from './livechat/livechat.component';
import { CoreModule } from './services/core.module';
import { NotTranslatedService } from './services/notTranslated.service';
import { ParameterHashLocationStrategy } from './services/ParameterHashLocationStrategy';
import State from './store/store';
import { LANGUAGES_API_URL } from './translations/i18n';
// AoT requires an exported function for factories
export class CustomTranslateLoader implements TranslateLoader {
  constructor(private http: HttpClient) {}
  getTranslation(lang: string): Observable<any> {
    // Carica prima il file locale
    const localTranslations = this.http
      .get(`./assets/translations/${lang}.json`)
      .pipe(catchError(() => of({})));

    const lokalise =
      `${LANGUAGES_API_URL}${lang}.json?cb=` + new Date().getTime();
    // Carica le traduzioni online
    const onlineTranslations = this.http.get(lokalise).pipe(
      catchError(() => of({})) // In caso di errore, restituisce un oggetto vuoto
    );
    return forkJoin({
      local: localTranslations,
      online: onlineTranslations,
    }).pipe(
      map(({ local, online }) => ({
        ...local,
        ...online, // Online sovrascrive i valori locali
      }))
    );
  }
}

@Injectable()
export class MyMissingTranslationHandler implements MissingTranslationHandler {
  handle(params: MissingTranslationHandlerParams) {
    return this.nts.instant(params.key);
  }

  constructor(private nts: NotTranslatedService) {}
}
// // AoT requires an exported function for factories
// export function HttpLoaderFactory(http: HttpClient) {
//   return new TranslateHttpLoader(http, './assets/languages/', '.json');
// }

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    HomePageComponent,
    FooterComponent,
    CertificatePageComponent,
    ReportPageComponent,
    PinPageComponent,
    RenewPageComponent,
    SuspendPageComponent,
    OtpPageComponent,
    LoginPageComponent,
    BccFederationComponent,
    Oauth2Component,
    ForcePWDPageComponent,
    FirstEmisPageComponent,
    ActivationStepsComponent,
    ActionReportPageComponent,
    RenewSuccessComponent,
    ContactSupportComponent,
    VirtualTokenComponent,
    VirtualTokenFormComponent,
    UiKitFieldWrapper,
    InputFieldType,
    InputPassworddType,
    TestPageComponent,
    ManageCertComponent,
    MultiCertComponent,
    ErrorPageComponent,
    EsgComponent,
    MigrationBannerComponent,
    MigrationComponent,
    LivechatComponent,
    ErrorModalComponent,
    AltTranslateDirective,
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    TranslateModule.forRoot({
      defaultLanguage: 'it_IT',
      loader: {
        provide: TranslateLoader,
        useFactory: (http: HttpClient) => new CustomTranslateLoader(http),
        //useClass: HttpTranslationLoader,
        deps: [HttpClient],
      },

      missingTranslationHandler: {
        provide: MissingTranslationHandler,
        useClass: MyMissingTranslationHandler,
        deps: [NotTranslatedService],
      },
      isolate: false,
      extend: true,
    }),
    NgUiKitModule.forRoot({}),
    SharedModule,
    CommonsModule,
    //NgxLocalStorageModule.forRoot(LocalStorageSettings),

    FormsModule,
    ToastNoAnimationModule.forRoot({
      maxOpened: 1,
      positionClass: 'toast-top-right',
      autoDismiss: true,
      enableHtml: true,
      //toastComponent: CustomToastComponent,
      messageClass: 'custom-toast',
      progressBar: true,
      timeOut: 5000,
    }),
    StoreModule.forRoot(State),
    ReactiveFormsModule,
    CoreModule,

    RecaptchaModule,
    BreadcrumbModule,
    FormlyModule.forRoot({
      extras: { renderFormlyFieldElement: false },
      // validationMessages: [
      //   { name: 'required', message: 'This field is required' },
      // ],
      // extensions: [
      //   {
      //     name: 'default-base-input',
      //     extension: new defaultBaseInputExtension(),
      //   },
      // ],

      validators: [
        { name: 'PASSWORD_STRENGHT', validation: strenghtPasswordValidatorFn },
        {
          name: 'PASSWORD_STRENGHT_MINOSSE',
          validation: strenghtPasswordMinosse,
        },
        { name: 'PIN_PUK_VALIDATOR', validation: pinPukValidator },
        { name: 'SAME_PASSWORD', validation: SameNewAndConfirmPassword },
        { name: 'SAME_PIN', validation: SamePinAndConfirmPin },
        { name: 'SAME_PIN_PUK', validation: samePinAndPuk },
        { name: 'SAME_OLD_PASSWORD', validation: sameOldPassword },
      ],

      wrappers: [{ name: 'UiKitWrapper', component: UiKitFieldWrapper }],
      types: [
        {
          name: 'ic1-text',
          component: InputFieldType,
          // wrappers: ['UiKitWrapper'],

          defaultOptions: {
            props: {
              //translate: true,
            },
            // className: 'ic1-form-group ',
          },
        },
        {
          name: 'ic1-password',
          component: InputPassworddType,
          extends: 'ic1-text',
        },
        {
          name: 'ic1-otp',
          component: InputOtpType,
          extends: 'ic1-text',
        },
      ],
      presets: [
        //Common password with strenght validator
        {
          name: 'password',
          config: {
            key: 'password',
            type: 'ic1-text',
            props: {
              type: 'password',
              label: 'HEADER.CHANGE_PWD.OLD_PWD_LABEL',
              placeholder: 'HEADER.CHANGE_PWD.OLD_PWD_PLACEHOLDER',
            },
          },
        },
      ],
    }),
    FormlyBootstrapModule,
    RecaptchaV3Module,
    OAuthModule.forRoot(),
    JwtModule.forRoot({
      config: {
        tokenGetter: () => {
          return localStorage.getItem('access_token');
        },
        allowedDomains: ['*'],
      },
    }),
    CommonModalComponent,
  ],
  providers: [
    provideHttpClient(withInterceptorsFromDi()),
    AuthGuard,
    CookieService,
    provideNgxWebstorage(
      withNgxWebstorageConfig({ prefix: 'ice-mysign', separator: '_' }),
      withLocalStorage(),
      withSessionStorage()
    ),
    {
      provide: LocationStrategy,
      useClass: ParameterHashLocationStrategy,
    },
    {
      provide: FORMLY_CONFIG,
      multi: true,
      useFactory: registerTranslateExtension,
      deps: [TranslateService],
    },
    {
      provide: RECAPTCHA_V3_SITE_KEY,
      useValue: environment.recaptcha.siteKey,
    },
    NotTranslatedService,
  ],
  bootstrap: [AppComponent],
  exports: [BreadcrumbsComponent],
})
export class AppModule {}
